import React, { useEffect, useState } from 'react'
import { string, shape, object, func, bool, array } from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { sortableContainer, sortableElement } from 'react-sortable-hoc'
import { Form, Select, Typography, Checkbox, Space } from 'antd'
import { DeleteOutlined } from '@ant-design/icons'
import styled from 'styled-components'

import { listRows } from 'states/modules/internalDatabaseDetails'
import { filterText } from 'services/filter'
import InfoField from 'components/infoField'
import Loader from 'components/loader'
import { LoaderContainer } from 'components/documentPreview/styles'

const { Paragraph } = Typography

const SortableElement = sortableElement(({ children }) => children)
const SortableContainer = sortableContainer(({ children }) => (
	<div>{children}</div>
))

const InternalDatabaseField = ({
	pageFieldsData,
	inputValue,
	disabled,
	onChange,
	form,
	visible,
	className,
	first,
}) => {
	const dispatch = useDispatch()
	const {
		databaseId,
		variable,
		type,
		id,
		list,
		label,
		optional,
		typeWidget,
		minimum,
		maximum,
	} = pageFieldsData
	const isObj = typeof variable === 'object'
	const varname = isObj ? variable.name : variable
	const name = id !== undefined ? `${varname}_${id}` : varname

	const { data: answer } = useSelector(({ answer }) => answer)
	const { dataRows, loading } = useSelector(({ internalDatabaseDetails }) => {
		const database = internalDatabaseDetails.databases.find(
			(database) => database.id === databaseId
		)
		return {
			dataRows: database?.items || [],
			loading: internalDatabaseDetails.loading,
		}
	})

	const initialValue = answer[name] ?? []
	const [values, setValues] = useState(initialValue)
	const [selectedItems, setSelectedItems] = useState([])

	useEffect(() => {
		dispatch(listRows({ id: databaseId, perPage: 100 }))
	}, [dispatch, databaseId])

	useEffect(() => {
		if (!loading && dataRows?.data && dataRows.data.length > 0) {
			setSelectedItems(
				values.map((selectedValue) => {
					return dataRows.data.find((row) => row.id === selectedValue.id)
				})
			)
		}
	}, [loading, dataRows, values])

	const handleChangeSelect = (e) => {
		setValues((prev) => {
			const selectedRow = dataRows?.data.find((item) => item.id === e)
			const newValue = {
				id: selectedRow.id,
				description: selectedRow.data[0].value,
			}
			const newValuesList = [...prev, newValue]
			form.setFieldsValue({
				[name]: newValuesList,
			})
			onChange(newValuesList)
			return newValuesList
		})
	}

	const handleDeleteSelectedText = (e) => {
		setValues((prev) => {
			const newValues = prev.filter((item) => item.id !== e)
			form.setFieldsValue({
				[name]: newValues,
			})
			onChange(newValues)
			return newValues
		})
	}

	const handleSort = ({ oldIndex, newIndex }) => {
		let newValues = [...values]
		newValues.splice(newIndex, 0, newValues.splice(oldIndex, 1)[0])

		setValues(newValues)
	}

	const options =
		dataRows?.data?.filter(
			(row) => !values.some((value) => value.id === row.id)
		) ?? []

	const notFoundContentText =
		dataRows === undefined
			? 'Nenhum dado encontrado'
			: loading
				? 'Dados sendo carregados'
				: 'Todos os dados foram utilizados'

	const renderList = () => {
		return (
			<>
				<SortableContainer onSortEnd={handleSort} distance={15}>
					{!loading
						? selectedItems.map(
								(row, index) =>
									row?.data[0]?.value && (
										<SortableElement key={row.id} index={index}>
											<TextSelected>
												<TextTitle>{row.data[0].value}</TextTitle>
												<DeleteOutlined
													style={{
														height: '100%',
														color: 'var(--ant-primary-color)',
														fontSize: 20,
													}}
													onClick={() => handleDeleteSelectedText(row.id)}
												/>
											</TextSelected>
										</SortableElement>
									)
							)
						: null}
				</SortableContainer>

				<Select
					showSearch
					maxTagCount={1}
					value={null}
					disabled={disabled}
					showArrow={false}
					filterOption={filterText}
					placeholder="Adicionar Texto"
					onChange={handleChangeSelect}
					autoFocus={first}
					notFoundContent={notFoundContentText}
					options={options.map((item) => {
						return { value: item.id, label: item.data[0].value }
					})}
				/>
			</>
		)
	}

	const renderCheckBox = () => {
		return (
			<Checkbox.Group disabled={disabled} onChange={onChange}>
				<Space direction="vertical" size={12}>
					{dataRows.data
						? dataRows.data.map((option, index) => (
								<Checkbox key={index} value={option}>
									{option.data[0].value}
								</Checkbox>
							))
						: null}
				</Space>
			</Checkbox.Group>
		)
	}

	const renderFormItem = () => {
		let info = ''
		if (minimum) {
			info = `Select at least ${minimum}`
		}
		if (maximum) {
			info = `Select at least ${maximum}`
		}
		if (minimum && maximum) {
			info = `Select at least ${minimum} and at most ${maximum}`
		}

		return (
			<Form.Item
				key={name}
				style={{ width: '100%' }}
				type={type}
				className={className}
				label={<InfoField label={label} info={info} />}
				name={list !== undefined ? [list, name] : name}
				initialValue={values}
				rules={
					visible && [
						() => ({
							validator(rule, value) {
								const max = Number(maximum) || undefined
								const min = Number(minimum) || undefined

								if (min && min > value.length)
									return Promise.reject(`Selecione no minímo ${min}.`)

								if (max && value.length > max)
									return Promise.reject(`Selecione no máximo ${max}.`)

								if (optional) return Promise.resolve()

								if (value.length === 0)
									return Promise.reject('Este campo é obrigatório.')

								return Promise.resolve()
							},
						}),
					]
				}
				colon={false}>
				{loading ? (
					<LoaderContainer>
						<Loader />
					</LoaderContainer>
				) : typeWidget === 'checkbox' ? (
					renderCheckBox()
				) : (
					renderList()
				)}
			</Form.Item>
		)
	}

	// TODO: for now, the first column of the database is considered as a key, hence, in some parts of the code there
	// is something in the shape "row.data[0].value"; in the future, must be changed for the actual key system
	return renderFormItem()
}

InternalDatabaseField.propTypes = {
	pageFieldsData: shape({
		label: string.isRequired,
		variable: object.isRequired,
		type: string.isRequired,
	}).isRequired,
	className: string,
	onChange: func,
	first: bool,
	inputValue: array,
	disabled: bool,
	visible: bool,
	form: object,
}

export default InternalDatabaseField

const TextSelected = styled.div`
	width: 100%;
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	border: 1px solid #d9d9d9;
	padding: 20px;
	margin-bottom: 15px;
	background: white;
`

const TextTitle = styled(Paragraph)`
	width: 90%;
	margin: 0 !important;

	font-size: 16px;
`
